import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from 'react-router-dom';
import useCart from '../../Hook/useCart';
import userAPI from '../../services/userAPI';
import cartAPI from '../../services/cartAPI';
import logo from '../../assets/waseeny-logo.png';
import { useTranslate } from '../../Hook/useTranslate';
import { useLang } from '../../context/LanguageContext';
import emptyCartImg from '../../assets/empty-cart.png';
import CitySelector from '../CitySelector/CitySelector';
import LocationPickerModal from '../Common/LocationPickerModal';
import { getCurrencyFromPhone } from '../../utils/phone';
import { formatMoney } from '../../utils/formatMoney';
import { getAbsoluteImageUrl } from '../../utils/imageUrl';
import defaultRestoImage from '../../assets/default-resto-image.png';
import defaultItemImage from '../../assets/default-item-image.png';
import { CheckoutSkeleton } from '../Common/PageSkeleton';
import FormBanner from '../Common/FormBanner';
import { getSessionToken } from '../../utils/authSession';
import './Checkout.css';

function CheckIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#48c479" strokeWidth="3">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#e62e2e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6"></polyline>
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
      <line x1="10" y1="11" x2="10" y2="17"></line>
      <line x1="14" y1="11" x2="14" y2="17"></line>
    </svg>
  );
}

function BackIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
      <path d="M14 5l-6 6 6 6" stroke="#222" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function BillRow({ label, value, valueColor = "#111" }) {
  return (
    <div className="bill-row" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '14px', color: '#666' }}>
      <span>{label}</span>
      <span style={{ color: valueColor, fontWeight: 700 }}>{value}</span>
    </div>
  );
}

export default function CheckoutPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    cart: backendCart,
    bill,
    loading: cartLoading,
    removeItem: hookRemoveItem,
    updateQuantity: hookUpdateQuantity,
    updateMeta,
    fetchCart,
    applyCoupon
  } = useCart();

  const [promoCode, setPromoCode] = useState("");
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoMessage, setPromoMessage] = useState({ text: "", type: "" });
  const [availableOffers, setAvailableOffers] = useState([]);
  const [fetchingOffers, setFetchingOffers] = useState(true);

  const t = useTranslate();
  const { lang } = useLang();
  const restaurant = backendCart?.restaurant;
  const currencySymbol = useMemo(() => getCurrencyFromPhone(restaurant?.contactNumber), [restaurant]);

  // Ensure user is logged in before viewing checkout
  useEffect(() => {
    const token = getSessionToken();
    if (!token) {
      if (window.confirm(t("Please log in for order"))) {
        navigate('/login', { state: { from: location } });
      } else {
        navigate('/home');
      }
    }
  }, [navigate, location, t]);

  // Fetch dynamic offers from Admin/Backend
  useEffect(() => {
    const fetchOffers = async () => {
      try {
        setFetchingOffers(true);
        const data = await cartAPI.getPublicCoupons();

        const currentRestoId = restaurant?._id || restaurant;

        // Filter offers STRICTLY for the current restaurant only (hiding platform-wide/other coupons)
        const filteredOffers = data.filter(promo => {
          if (!promo.restaurant || !currentRestoId) return false;
          const promoRestoId = typeof promo.restaurant === 'object' ? (promo.restaurant._id || promo.restaurant) : promo.restaurant;
          return promoRestoId?.toString() === currentRestoId?.toString();
        });

        // Map backend data to our UI structure
        const mappedOffers = filteredOffers.map(promo => ({
          code: promo.code,
          badge: promo.offerType === 'percent' ? `${promo.discountValue}% OFF` : `${formatMoney(promo.discountValue, { symbol: currencySymbol })} OFF`,
          upto: promo.maxDiscountAmount > 0 ? `Up to ${formatMoney(promo.maxDiscountAmount, { symbol: currencySymbol })}` : "No Max Limit",
          description: promo.description || `Special offer for you!`,
          color: promo.offerType === 'percent' ? '#ef4444' : '#48c479' // percent: red, flat: green
        }));

        setAvailableOffers(mappedOffers);
      } catch (err) {
        console.error("Failed to fetch offers:", err);
      } finally {
        setFetchingOffers(false);
      }
    };

    if (restaurant) {
      fetchOffers();
    }
  }, [restaurant, currencySymbol]);

  const handleApplyPromo = async (codeToApply) => {
    const code = codeToApply || promoCode;
    if (!code) return;

    setPromoLoading(true);
    setPromoMessage({ text: "", type: "" });
    try {
      await applyCoupon(code);
      setPromoMessage({ text: "Coupon applied successfully!", type: "success" });
      setPromoCode(code);
    } catch (err) {
      setPromoMessage({ text: err.message || "Invalid coupon code", type: "error" });
    } finally {
      setPromoLoading(false);
    }
  };

  const [addressAdded, setAddressAdded] = useState(false);
  const [addressLine, setAddressLine] = useState('');
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [localItems, setLocalItems] = useState([]);
  const isAuthenticated = getSessionToken() || localStorage.getItem('waseeny_isAuthenticated') === 'true';
  const [userData, setUserData] = useState(null);

  const [houseNo, setHouseNo] = useState("");
  const [street, setStreet] = useState("");
  const [landmark, setLandmark] = useState("");
  const [addressType, setAddressType] = useState("Home");
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [mapCenter, setMapCenter] = useState({ lat: 22.7196, lng: 75.8577 });
  const [currentCoords, setCurrentCoords] = useState([75.8577, 22.7196]);
  const [selectedCity, setSelectedCity] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formBanner, setFormBanner] = useState({ type: '', text: '' });

  const updateLocationData = async (lat, lng, addressString) => {
    setMapCenter({ lat, lng });
    setCurrentCoords([lng, lat]);
    setAddressLine(addressString);

    if (addressString) {
      const parts = addressString.split(',').map(p => p.trim());
      if (parts.length > 0) {
        setHouseNo(parts[0] || '');
        setStreet(parts.length > 1 ? parts[1] : '');
        if (parts.length > 2) {
          setLandmark(parts[2]);
        }
      }
    }
  };

  const handleAddressSubmit = async () => {
    if (!houseNo.trim() || !street.trim() || !addressLine.trim()) {
      setFormBanner({
        type: 'error',
        text: t('Please fill all mandatory fields and select a location on the map.')
      });
      return;
    }
    try {
      setIsSubmitting(true);
      setFormBanner({ type: '', text: '' });
      const fullAddr = `${houseNo}, ${street}${landmark ? `, ${t("Near")} ${landmark}` : ''}, ${addressLine}`;
      const res = await userAPI.addAddress({
        label: addressType,
        addressLine: fullAddr,
        houseNo: houseNo.trim(),
        streetArea: street.trim(),
        landmark: landmark.trim(),
        city: selectedCity?.name || (addressLine.split(',').slice(-2, -1)[0]?.trim()) || 'Unknown',
        state: selectedCity?.stateName || selectedCity?.state || '',
        stateCode: selectedCity?.stateCode || '',
        country: selectedCity?.countryName || selectedCity?.country || '',
        countryCode: selectedCity?.countryCode || '',
        zipCode: selectedCity?.postalCode || selectedCity?.zipCode || '',
        postalCode: selectedCity?.postalCode || selectedCity?.zipCode || '',
        location: { type: "Point", coordinates: currentCoords },
        deliveryInstructions: "",
        isDefault: true
      });
      const newAddrId = res.addresses?.[res.addresses.length - 1]?._id || res.address?._id;
      if (newAddrId) {
        setSelectedAddressId(newAddrId);
        if (updateMeta) await updateMeta({ address: `${addressType} | ${fullAddr}`, addressId: newAddrId });
        if (fetchCart) fetchCart(newAddrId);
        setAddressLine(fullAddr); // Show the final saved address
      }
      setAddressAdded(true);
      setIsAddingNew(false);
    } catch (err) {
      setFormBanner({
        type: 'error',
        text: t("Failed to save address. Please try again.")
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const data = await userAPI.getProfile();
        setUserData(data);
        if (data?.savedAddresses?.length > 0 && !selectedAddressId) {
          const defaultAddr = data.savedAddresses.find(a => a.isDefault) || data.savedAddresses[0];
          if (defaultAddr) {
            setSelectedAddressId(defaultAddr._id);
            setAddressLine(defaultAddr.addressLine);
            setAddressAdded(true);
          }
        }
      } catch (err) { }
    };
    if (isAuthenticated) fetchUser();
  }, [isAuthenticated, selectedAddressId]);

  useEffect(() => {
    if (isAuthenticated && backendCart?.items) {
      setLocalItems(backendCart.items);
    }
  }, [backendCart, isAuthenticated]);

  useEffect(() => {
    if (backendCart?.meta?.addressId) {
      setSelectedAddressId(backendCart.meta.addressId);
      setAddressAdded(true);
      setAddressLine(backendCart.meta.addressLine || backendCart.meta.address || "");
    }
  }, [backendCart]);

  const handleProceedToPayment = async () => {
    if (!selectedAddressId && !addressLine) {
      setFormBanner({
        type: 'error',
        text: t("Please select or add a delivery address first.")
      });
      setIsAddingNew(true);
      return;
    }
    setFormBanner({ type: '', text: '' });
    try {
      if (updateMeta && selectedAddressId) {
        await updateMeta({
          address: addressLine,
          addressId: selectedAddressId
        });
      }
    } catch (e) {
      console.warn("Could not update cart meta:", e);
    }
    navigate('/payment', { state: { addressId: selectedAddressId } });
  };

  const toMoneyNumber = (value, fallback = 0) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : fallback;
  };

  const calculatedSubtotal = localItems.reduce((acc, item) => acc + (item.price * item.quantity), 0);

  // Calculate scaling factor if backend is in different currency (e.g. EUR vs INR)
  // This happens because backend defaults to EUR in getCart if no currency is passed
  const backendRawItemTotal = toMoneyNumber(bill?.itemTotal);
  const scalingFactor = (backendRawItemTotal > 0 && calculatedSubtotal > 0)
    ? (calculatedSubtotal / backendRawItemTotal)
    : 1;

  const groupedItems = useMemo(() => {
    const groupedMap = new Map();
    localItems.forEach((item) => {
      const groupKey = item.productId || item._id;
      const existing = groupedMap.get(groupKey);
      if (existing) {
        existing.quantity += item.quantity;
        existing.totalPrice += item.price * item.quantity;
      } else {
        groupedMap.set(groupKey, { ...item, groupKey, totalPrice: item.price * item.quantity, sourceItems: [item] });
      }
    });
    return Array.from(groupedMap.values());
  }, [localItems]);

  const sortedAddresses = useMemo(() => {
    if (!userData?.savedAddresses) return [];
    return [...userData.savedAddresses]
      .sort((a, b) => (b.usageCount || 0) - (a.usageCount || 0))
      .slice(0, 5);
  }, [userData]);

  const backendItemTotal = calculatedSubtotal; // Always use local calculation for consistency with item list
  const serviceFee = toMoneyNumber(bill?.platformFee) * scalingFactor;
  const deliveryFee = toMoneyNumber(bill?.deliveryFee) * scalingFactor;
  const tax = toMoneyNumber(bill?.tax) * scalingFactor;
  const packaging = toMoneyNumber(bill?.packaging) * scalingFactor;
  const smallCartFee = toMoneyNumber(bill?.smallCartFee) * scalingFactor;
  const surgeFee = toMoneyNumber(bill?.surgeFee) * scalingFactor;
  const tip = toMoneyNumber(bill?.tip) * scalingFactor;
  const discountAmount = toMoneyNumber(bill?.discount) * scalingFactor;

  const calculatedGrandTotal = (
    backendItemTotal +
    serviceFee +
    deliveryFee +
    tax +
    packaging +
    smallCartFee +
    surgeFee +
    tip -
    discountAmount
  );
  const grandTotal = calculatedGrandTotal;

  const getLangText = (data) => {
    if (!data) return "";
    if (typeof data === 'string') return data;
    return data[lang] || data.en || (data && Object.values(data)[0]) || "";
  };

  const handleQuantityChange = async (groupedItem, action) => {
    const targetItem = groupedItem?.sourceItems?.[0];
    if (!targetItem) return;
    try {
      const newQty = action === 'increase' ? targetItem.quantity + 1 : Math.max(1, targetItem.quantity - 1);
      setLocalItems(prev => prev.map(item => item._id === targetItem._id ? { ...item, quantity: newQty } : item));
      await hookUpdateQuantity(targetItem._id, newQty);
    } catch (err) { }
  };

  const handleRemoveItem = async (groupedItem) => {
    const targetItem = groupedItem?.sourceItems?.[0];
    if (!targetItem) return;
    try {
      setLocalItems(prev => prev.filter(item => item._id !== targetItem._id));
      await hookRemoveItem(targetItem._id);
    } catch (err) { }
  };

  if (cartLoading) return <CheckoutSkeleton />;

  return (
    <div className="checkout-container">
      <header className="checkout-header">
        <div className="checkout-header-content">
          <div className="checkout-logo-section">
            <button onClick={() => navigate(-1)} className="back-btn" style={{ marginRight: '10px' }}>
              <BackIcon />
            </button>
            <img src={logo} alt="Waseeny" className="logo-image-small" onClick={() => navigate('/home')} />
            <span className="checkout-main-title">{t("Checkout")}</span>
          </div>
          <div className="checkout-header-actions">
            <div className="header-action-item">
              <span>{userData?.name || "User"}</span>
            </div>
          </div>
        </div>
      </header>

      {localItems.length === 0 ? (
        <div className="empty-cart-container">
          <div className="empty-cart-card">
            <img src={emptyCartImg} alt="Empty Cart" className="empty-cart-img" />
            <h2 className="empty-cart-title">{t("cartEmpty")}</h2>
            <p className="empty-cart-subtitle">{t("Looks like you haven't added anything to your cart yet.")}</p>
            <button className="go-shop-btn" onClick={() => navigate('/home')}>{t("Browse Restaurants")}</button>
          </div>
        </div>
      ) : (
        <main className="checkout-content">
          <FormBanner
            message={formBanner.text}
            type={formBanner.type || 'error'}
            onDismiss={() => setFormBanner({ type: '', text: '' })}
          />
          <div className="checkout-main">
            {!isAuthenticated ? (
              <div className="login-card-premium">
                <h2 className="login-prompt-title">{t("pleaseLogInToOrder")}</h2>
                <p className="login-prompt-subtitle">{t("loginRequiredForOrder")}</p>
                <button onClick={() => navigate('/login', { state: { from: location } })} className="login-btn-large">
                  {t("logInOrCreateAccount")}
                </button>
              </div>
            ) : isAddingNew ? (
              <section className="address-section-premium">
                <div className="address-section-header">
                  <div className="header-text">
                    <h2>{t("addNewAddress") || "Add Delivery Address"}</h2>
                    <p>{t("Please provide your delivery location details")}</p>
                  </div>
                  {sortedAddresses?.length > 0 && (
                    <button
                      onClick={() => setIsAddingNew(false)}
                      style={{
                        background: '#f1f5f9',
                        border: 'none',
                        padding: '8px 14px',
                        borderRadius: '8px',
                        fontSize: '13px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        color: '#475569'
                      }}
                    >
                      ← {t("Back to Saved Addresses") || "Back"}
                    </button>
                  )}
                </div>

                <div className="address-form-container">
                  <CitySelector onCitySelect={setSelectedCity} selectedCity={selectedCity} />

                  <div style={{ marginTop: '20px', marginBottom: '20px' }}>
                    <button
                      className="use-location-btn"
                      onClick={() => setIsLocationModalOpen(true)}
                    >
                      {addressLine ? t("Change Map Location") : t("Select Location on Map")}
                    </button>
                    {addressLine && (
                      <div style={{ fontSize: '13px', color: '#666', marginTop: '10px', padding: '12px', background: '#fef2f2', borderRadius: '8px', border: '1px solid #fee2e2' }}>
                        <strong style={{ color: '#ef4444' }}>{t("Selected Location:")}</strong> {addressLine}
                      </div>
                    )}
                  </div>

                  <LocationPickerModal
                    isOpen={isLocationModalOpen}
                    onClose={() => setIsLocationModalOpen(false)}
                    initialCenter={mapCenter}
                    onConfirm={async (data) => {
                      await updateLocationData(data.lat, data.lng, data.address);
                      setIsLocationModalOpen(false);
                    }}
                  />

                  <div className="form-group" style={{ display: 'flex', gap: '15px' }}>
                    <div style={{ flex: 1 }}>
                      <label>{t("House / Flat No.")} *</label>
                      <input
                        type="text"
                        className="premium-input"
                        placeholder={t("e.g. Flat 101")}
                        value={houseNo}
                        onChange={(e) => setHouseNo(e.target.value)}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label>{t("Street / Area")} *</label>
                      <input
                        type="text"
                        className="premium-input"
                        placeholder={t("e.g. Main Street")}
                        value={street}
                        onChange={(e) => setStreet(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>{t("Landmark (Optional)")}</label>
                    <input
                      type="text"
                      className="premium-input"
                      placeholder={t("e.g. Near Apollo Hospital")}
                      value={landmark}
                      onChange={(e) => setLandmark(e.target.value)}
                    />
                  </div>

                  <div className="form-group">
                    <label>{t("Save As")}</label>
                    <select
                      className="premium-select"
                      value={addressType}
                      onChange={(e) => setAddressType(e.target.value)}
                    >
                      <option value="Home">{t("homeType")}</option>
                      <option value="Work">{t("workType")}</option>
                      <option value="Other">{t("otherType")}</option>
                    </select>
                  </div>

                  <div className="form-actions">
                    <button className="save-address-btn" onClick={handleAddressSubmit} disabled={isSubmitting}>
                      {isSubmitting ? t("Saving...") : t("Save Address")}
                    </button>
                    {sortedAddresses?.length > 0 && (
                      <button className="cancel-address-btn" onClick={() => setIsAddingNew(false)}>{t("Cancel")}</button>
                    )}
                  </div>
                </div>
              </section>
            ) : (
              <section className="address-section-premium">
                <div className="address-section-header">
                  <div className="header-text">
                    <h2>{t("selectDeliveryAddress") || "Select Delivery Address"}</h2>
                    <p>{t("Choose a saved address or add a new one")}</p>
                  </div>
                </div>

                {/* ── Saved Addresses List ── */}
                {sortedAddresses?.length > 0 ? (
                  <div className="saved-addresses-list-checkout" style={{ marginBottom: '24px' }}>
                    {sortedAddresses.map((addr) => {
                      const isSelected = selectedAddressId === addr._id;
                      return (
                        <div
                          key={addr._id}
                          className={`saved-address-card-checkout ${isSelected ? 'selected' : ''}`}
                          onClick={async () => {
                            setSelectedAddressId(addr._id);
                            setAddressLine(addr.addressLine);
                            setAddressAdded(true);
                            if (updateMeta) await updateMeta({
                              address: `${addr.label} | ${addr.addressLine}`,
                              addressId: addr._id
                            });
                            if (fetchCart) fetchCart(addr._id);
                          }}
                          style={{
                            padding: '16px',
                            border: isSelected ? '2px solid #C5192D' : '1px solid #e2e8f0',
                            borderRadius: '16px',
                            marginBottom: '12px',
                            cursor: 'pointer',
                            background: isSelected ? '#fff5f5' : '#ffffff',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '14px',
                            boxShadow: isSelected ? '0 4px 15px rgba(197, 25, 45, 0.08)' : '0 2px 6px rgba(0,0,0,0.02)'
                          }}
                        >
                          <div style={{
                            width: '22px',
                            height: '22px',
                            borderRadius: '50%',
                            border: isSelected ? '6px solid #C5192D' : '2px solid #cbd5e1',
                            background: '#ffffff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                          }} />

                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 700, fontSize: '15px', color: '#0f172a', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span>{addr.label === 'Home' ? '🏠' : addr.label === 'Work' ? '💼' : '📍'} {addr.label}</span>
                              {addr.isDefault && <span style={{ fontSize: '10px', background: '#fef2f2', color: '#ef4444', padding: '2px 8px', borderRadius: '6px', border: '1px solid #fee2e2', fontWeight: 800 }}>{t("DEFAULT") || "DEFAULT"}</span>}
                            </div>
                            <div style={{ fontSize: '13px', color: '#64748b', lineHeight: '1.4' }}>{addr.addressLine}</div>
                          </div>

                          {isSelected && (
                            <div style={{ color: '#C5192D', display: 'flex', alignItems: 'center' }}>
                              <CheckIcon />
                            </div>
                          )}
                        </div>
                      );
                    })}

                    <button
                      onClick={() => setIsAddingNew(true)}
                      style={{
                        width: '100%',
                        padding: '14px',
                        border: '1.5px dashed #cbd5e1',
                        borderRadius: '14px',
                        background: '#f8fafc',
                        color: '#334155',
                        fontSize: '14px',
                        fontWeight: 700,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px'
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#C5192D'; e.currentTarget.style.color = '#C5192D'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.color = '#334155'; }}
                    >
                      + {t("addNewAddress") || "Add New Address"}
                    </button>

                    {/* Main Proceed to Payment Button */}
                    <div style={{ marginTop: '24px' }}>
                      <button
                        className="checkout-primary-proceed-btn"
                        onClick={handleProceedToPayment}
                        disabled={!selectedAddressId && !addressLine}
                      >
                        <span>{t("proceedToPay") || "Proceed to Payment"}</span>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '30px 20px', background: '#ffffff', borderRadius: '16px', border: '1px solid #e2e8f0' }}>
                    <p style={{ color: '#64748b', marginBottom: '16px' }}>No saved addresses found.</p>
                    <button
                      onClick={() => setIsAddingNew(true)}
                      style={{
                        padding: '12px 24px',
                        background: '#C5192D',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '12px',
                        fontWeight: 700,
                        cursor: 'pointer'
                      }}
                    >
                      + {t("addNewAddress") || "Add New Address"}
                    </button>
                  </div>
                )}
              </section>
            )}
          </div>

          <aside className="checkout-sidebar">
            <div className="order-summary-card">
              <div className="rest-header">
                <img src={getAbsoluteImageUrl(restaurant?.image) || defaultRestoImage} alt="" className="checkout-rest-img" />
                <div className="rest-name">{restaurant ? getLangText(restaurant.name) : t('Apna Resto')}</div>
              </div>

              <div className="items-list">
                {groupedItems.map(item => (
                  <div key={item.groupKey} className="item-row-v2">
                    <div className="item-img-container">
                      <img
                        src={getAbsoluteImageUrl(item.image) || defaultItemImage}
                        alt={getLangText(item.name)}
                        className="checkout-item-img"
                        onError={(e) => { e.target.src = defaultItemImage; }}
                      />
                    </div>
                    <div className="item-left">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span className="item-name">{getLangText(item.name)}</span>
                        <button
                          className="delete-item-btn"
                          onClick={() => handleRemoveItem(item)}
                          title={t("Remove item")}
                        >
                          <TrashIcon />
                        </button>
                      </div>
                      <div className="qty-controls">
                        <button onClick={() => handleQuantityChange(item, 'decrease')}>-</button>
                        <span className="qty-val">{item.quantity}</span>
                        <button onClick={() => handleQuantityChange(item, 'increase')}>+</button>
                      </div>
                    </div>
                    <span className="item-price-val">{formatMoney(item.totalPrice, { symbol: currencySymbol })}</span>
                  </div>
                ))}
              </div>

              <div className="checkout-offers-card">
                <div className="offers-section-header-v3">
                  <span className="offers-icon-v3">🎟️</span>
                  <span className="offers-title-v3">{t("Available Offers & Coupons")}</span>
                </div>

                <div className="offers-list-v2">
                  {fetchingOffers ? (
                    <div className="offers-loading-v3">{t("Loading offers...")}</div>
                  ) : availableOffers.length > 0 ? (
                    availableOffers.map((offer) => (
                      <div
                        key={offer.code}
                        className={`coupon-card-mini ${promoCode === offer.code ? 'active' : ''}`}
                        onClick={() => handleApplyPromo(offer.code)}
                      >
                        <div className="coupon-left-mini" style={{ backgroundColor: offer.color }}>
                          <div className="discount-badge-mini">{offer.badge}</div>
                          <div className="scissors-line-mini"></div>
                        </div>
                        <div className="coupon-right-mini">
                          <div className="coupon-main-info">
                            <h4 className="coupon-code-mini">{offer.code}</h4>
                            <p className="coupon-upto-mini">{offer.upto}</p>
                            <p className="coupon-desc-mini">{offer.description}</p>
                          </div>
                          <div className="coupon-action-mini">
                            <button className="apply-coupon-btn-mini">
                              {promoCode === offer.code ? t("APPLIED") : t("APPLY")}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="no-offers-v3">{t("No coupons available right now.")}</div>
                  )}
                </div>

                <div className="promo-input-container-v3">
                  {promoMessage.text && (
                    <div className={`promo-status-msg-v3 ${promoMessage.type}`}>
                      {promoMessage.type === 'success' ? '🎉 ' : '⚠️ '}{promoMessage.text}
                    </div>
                  )}
                </div>
              </div>

              <div className="billing-box">
                <BillRow label="Item Total" value={formatMoney(backendItemTotal, { symbol: currencySymbol })} />
                <BillRow label="Delivery Fee" value={deliveryFee > 0 ? formatMoney(deliveryFee, { symbol: currencySymbol }) : "FREE"} valueColor={deliveryFee > 0 ? "#111" : "#48c479"} />
                <BillRow label="Platform Fee" value={formatMoney(serviceFee, { symbol: currencySymbol })} />
                {tax > 0 && <BillRow label="Tax" value={formatMoney(tax, { symbol: currencySymbol })} />}
                {packaging > 0 && <BillRow label="Packaging" value={formatMoney(packaging, { symbol: currencySymbol })} />}
                {smallCartFee > 0 && <BillRow label="Small Cart Fee" value={formatMoney(smallCartFee, { symbol: currencySymbol })} />}
                {surgeFee > 0 && <BillRow label="Surge Fee" value={formatMoney(surgeFee, { symbol: currencySymbol })} />}
                {tip > 0 && <BillRow label="Tip" value={formatMoney(tip, { symbol: currencySymbol })} />}
                {discountAmount > 0 && <BillRow label="Discount" value={`-${formatMoney(discountAmount, { symbol: currencySymbol })}`} valueColor="#48c479" />}
                <div className="grand-total" style={{ display: 'flex', justifyContent: 'space-between', marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #eee' }}>
                  <span style={{ fontSize: '16px', fontWeight: 700, color: '#111' }}>Grand Total</span>
                  <span style={{ fontSize: '18px', fontWeight: 800, color: '#111' }}>{formatMoney(grandTotal, { symbol: currencySymbol })}</span>
                </div>
              </div>

              {/* Sidebar Proceed to Payment Button */}
              <div style={{ marginTop: '16px' }}>
                <button
                  className="checkout-sidebar-proceed-btn"
                  onClick={handleProceedToPayment}
                  disabled={!selectedAddressId && !addressLine}
                >
                  <span>{t("proceedToPay") || "Proceed to Payment"}</span>
                  <span className="checkout-sidebar-btn-price">{formatMoney(grandTotal, { symbol: currencySymbol })} →</span>
                </button>
              </div>
            </div>
          </aside>
        </main>
      )}
    </div>
  );
}
